import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './IntegrationApplicationMasterData.css';

interface APRStats {
  totalModels: number;
  createdCount: number;
  scheduledCount: number;
  inProcessCount: number;
  completedCount: number;
  totalRuns: number;
  lastRunDate?: string;
  assignedApps: number;
  unassignedApps: number;
}

const APRRationalizationModels: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<APRStats>({
    totalModels: 0,
    createdCount: 0,
    scheduledCount: 0,
    inProcessCount: 0,
    completedCount: 0,
    totalRuns: 0,
    assignedApps: 0,
    unassignedApps: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAPRStats();
  }, []);

  const loadAPRStats = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel (single query for app stats, no N+1)
      const [modelsData, aprRuns, appStats] = await Promise.all([
        apiService.getModels(),
        apiService.getAPRRuns().catch(() => []),
        apiService.getModelApplicationStats().catch(() => ({ assigned: 0, unassigned: 0 }))
      ]);
      
      // Calculate model counts by status
      const createdCount = modelsData.filter((model: any) => model.status === 'Created').length;
      const scheduledCount = modelsData.filter((model: any) => model.status === 'Scheduled').length;
      const inProcessCount = modelsData.filter((model: any) => model.status === 'In-Process').length;
      const completedCount = modelsData.filter((model: any) => model.status === 'Completed').length;
      
      // Get last run date
      let lastRunDate = null;
      if (Array.isArray(aprRuns) && aprRuns.length > 0) {
        // Sort runs by date and get the most recent
        const sortedRuns = [...aprRuns].sort((a: any, b: any) => 
          new Date(b.createdDate || b.created_at || 0).getTime() - 
          new Date(a.createdDate || a.created_at || 0).getTime()
        );
        if (sortedRuns[0]) {
          lastRunDate = new Date(sortedRuns[0].createdDate || sortedRuns[0].created_at).toLocaleDateString();
        }
      }
      
      setStats({
        totalModels: modelsData.length,
        createdCount,
        scheduledCount,
        inProcessCount,
        completedCount,
        totalRuns: Array.isArray(aprRuns) ? aprRuns.length : 0,
        lastRunDate: lastRunDate || undefined,
        assignedApps: appStats.assigned,
        unassignedApps: appStats.unassigned
      });
    } catch (error) {
      // Fallback to empty stats on error
      setStats({
        totalModels: 0,
        createdCount: 0,
        scheduledCount: 0,
        inProcessCount: 0,
        completedCount: 0,
        totalRuns: 0,
        assignedApps: 0,
        unassignedApps: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>APR Rationalization Models</h1>
          <p>Loading APR model data...</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">APR Rationalization Models</h1>
            <p className="page-header__subtitle">
              Overview and management of Application Portfolio Rationalization models
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

        {/* APR Model Statistics */}
        <div className="md-card">
          <div className="md-card-content">
            <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>📊 APR Model Statistics</h2>
            <div className="md-stats-grid">
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary">{formatNumber(stats.totalModels)}</div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Total Models</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    All APR models in system
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#4CAF50' }}>
                    {formatNumber(stats.createdCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Created</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Ready to execute
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#FF9800' }}>
                    {formatNumber(stats.inProcessCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>In Progress</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Currently running
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#8BC34A' }}>
                    {formatNumber(stats.completedCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Completed</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Analysis finished
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#9C27B0' }}>
                    {formatNumber(stats.totalRuns)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Total Runs</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    All executions
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#607D8B' }}>
                    📅
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Last Run</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.lastRunDate || 'No recent runs'}
                  </div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#2196F3' }}>
                    {formatNumber(stats.assignedApps)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Assigned Apps</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    In APR models
                  </div>
                </div>
              </div>

              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#f59e0b' }}>
                    {formatNumber(stats.unassignedApps)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Unassigned Apps</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Not in any model
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model Status Distribution */}
        <div className="md-card">
          <div className="md-card-content">
            <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🎯 Model Status Distribution</h2>
            <div className="md-stats-grid">
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#4CAF50' }}>
                    {formatNumber(stats.createdCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>📝 Created</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalModels > 0 
                      ? `${Math.round((stats.createdCount / stats.totalModels) * 100)}% of total models` 
                      : '0% of total models'}
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#2196F3' }}>
                    {formatNumber(stats.scheduledCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>⏰ Scheduled</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalModels > 0 
                      ? `${Math.round((stats.scheduledCount / stats.totalModels) * 100)}% of total models` 
                      : '0% of total models'}
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#FF9800' }}>
                    {formatNumber(stats.inProcessCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>⚡ Running</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalModels > 0 
                      ? `${Math.round((stats.inProcessCount / stats.totalModels) * 100)}% of total models` 
                      : '0% of total models'}
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated">
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#8BC34A' }}>
                    {formatNumber(stats.completedCount)}
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>✅ Completed</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {stats.totalModels > 0 
                      ? `${Math.round((stats.completedCount / stats.totalModels) * 100)}% of total models` 
                      : '0% of total models'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="md-card">
          <div className="md-card-content">
            <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🚀 Quick Actions</h2>
            <div className="md-stats-grid">
              <div className="md-card md-card--elevated" style={{ cursor: 'pointer' }} onClick={() => navigate('/apr-model')}>
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#2196F3', fontSize: '24px' }}>
                    📝
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>APR Model Definition</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Define and manage APR models
                  </div>
                </div>
              </div>
              
              <div className="md-card md-card--elevated" style={{ cursor: 'pointer' }} onClick={() => navigate('/execution')}>
                <div className="md-card-content">
                  <div className="md-stat-primary" style={{ color: '#4CAF50', fontSize: '24px' }}>
                    ▶️
                  </div>
                  <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>APR Model Execution</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Execute APR analysis models
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APRRationalizationModels;